import React, { useEffect, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { Plus, Edit2, Trash2, GitFork, Palette } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const qs = action.payload;
    const newQs = [];
    qs.forEach((q) => {
      const idx = state.findIndex((x) => x.id === q.id);
      if (idx !== -1) state[idx] = q; else newQs.push(q);
    });
    return [...state, ...newQs];
  }
  if (action.type === "UPDATE_QUEUES") {
    const q = action.payload;
    const idx = state.findIndex((x) => x.id === q.id);
    if (idx !== -1) { state[idx] = q; return [...state]; }
    return [q, ...state];
  }
  if (action.type === "DELETE_QUEUE") return state.filter((q) => q.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

const Queues = () => {
  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) { toastError(err); }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    const handleQueue = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      if (data.action === "delete")
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
    };

    socket.on("queue", handleQueue);

    return () => {
      socket.off("queue", handleQueue);
    };
  }, []);

  const handleDeleteQueue = async (id) => {
    try {
      await api.delete(`/queue/${id}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) { toastError(err); }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={selectedQueue ? `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?` : ""}
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setSelectedQueue(null); }}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={() => { setQueueModalOpen(false); setSelectedQueue(null); }}
        queueId={selectedQueue?.id}
      />

      <MainHeader>
        <Title>{i18n.t("queues.title")}</Title>
        <MainHeaderButtonsWrapper>
          <button
            onClick={() => { setQueueModalOpen(true); setSelectedQueue(null); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            {i18n.t("queues.buttons.add")}
          </button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                {[i18n.t("queues.table.name"), i18n.t("queues.table.color"), i18n.t("queues.table.greeting"), i18n.t("queues.table.actions")].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap ${i === 2 ? "text-left" : i === 0 ? "text-left" : "text-center"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {queues.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center">
                    <GitFork className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">No hay colas</p>
                    <p className="text-sm text-slate-400 mt-1">Crea tu primera cola para organizar conversaciones</p>
                  </td>
                </tr>
              ) : (
                queues.map((queue) => (
                  <tr key={queue.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                        style={{ backgroundColor: `${queue.color}20`, color: queue.color, border: `1px solid ${queue.color}40` }}
                      >
                        <GitFork className="w-3 h-3" />
                        {queue.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-white text-xs font-semibold"
                        style={{ backgroundColor: queue.color }}
                        title={queue.color}
                      >
                        <Palette className="w-3 h-3" />
                        {queue.color}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-slate-600 dark:text-slate-400 text-sm truncate">
                        {queue.greetingMessage || <span className="text-slate-300 dark:text-slate-600 italic">Sin mensaje de saludo</span>}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setSelectedQueue(queue); setQueueModalOpen(true); }} title="Editar" className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setSelectedQueue(queue); setConfirmModalOpen(true); }} title="Eliminar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {loading && (
                <tr><td colSpan={4} className="py-4 text-center"><div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default Queues;
